import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail, friendlyError } from "../lib/api";
import { ShieldCheck, Clock, X, Instagram, Mail, Calendar, User2, LogOut, Settings, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, loading, logout, refresh } = useAuth();
  const [bio, setBio] = useState(user?.bio || "");
  const [editingBio, setEditingBio] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setBio(user?.bio || ""); }, [user?.bio]);
  if (loading) return <div className="min-h-screen bg-paper grid place-items-center text-slate-500">Načítám…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const status = user.verification_status;
  const statusBadge = {
    approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <ShieldCheck className="w-4 h-4" />, label: "Ověřený profil" },
    submitted: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock className="w-4 h-4" />, label: "Čeká na ověření" },
    rejected: { cls: "bg-red-50 text-red-700 border-red-200", icon: <X className="w-4 h-4" />, label: "Ověření zamítnuto" },
    pending: { cls: "bg-slate-100 text-slate-600 border-slate-200", icon: <Clock className="w-4 h-4" />, label: "Občanka nenahraná" },
  }[status] || { cls: "bg-slate-100 text-slate-600 border-slate-200", icon: <Clock className="w-4 h-4" />, label: status };

  return (
    <div className="min-h-screen bg-paper py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="font-heading font-extrabold text-2xl tracking-tight text-slate-900 flex items-center gap-0.5 mb-8" data-testid="profile-back-logo">
          ConnectYou<span className="text-sky-500">.</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-sky-100 text-sky-700 grid place-items-center font-heading font-extrabold text-2xl">
                {user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid="profile-name">{user.name}</h1>
                <span data-testid="profile-status-badge" className={`mt-1 inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${statusBadge.cls}`}>
                  {statusBadge.icon} {statusBadge.label}
                </span>
              </div>
            </div>
            <button
              data-testid="profile-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold transition-colors"
            >
              <LogOut className="w-4 h-4" /> Odhlásit
            </button>
          </div>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Item icon={<Mail className="w-4 h-4" />} label="Hlavní e-mail" value={user.email} />
            {user.marketing_email && (
              <Item icon={<Mail className="w-4 h-4" />} label="Marketing e-mail" value={user.marketing_email} />
            )}
            <Item icon={<User2 className="w-4 h-4" />} label="Jméno" value={user.name} />
            <Item icon={<Calendar className="w-4 h-4" />} label="Věk" value={user.age} />
            <Item icon={<Instagram className="w-4 h-4" />} label="Instagram" value={user.instagram_handle ? `@${user.instagram_handle}` : "—"} />
          </div>

          {/* BIO — optional, editable */}
          <div className="mt-5 rounded-2xl border border-slate-200 p-4 bg-slate-50/40" data-testid="profile-bio-section">
            <div className="flex items-center gap-1.5 text-slate-500 text-xs uppercase tracking-[0.12em] font-bold">
              <Sparkles className="w-4 h-4" /> Něco o sobě (volitelné, max 200 znaků)
            </div>
            {editingBio ? (
              <div className="mt-2">
                <textarea
                  data-testid="profile-bio-textarea"
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 200))}
                  rows={3}
                  placeholder="Třeba: 16 let, miluju cestování a metalovou hudbu."
                  className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium resize-none"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-400 font-bold">{bio.length}/200</span>
                  <div className="flex gap-2">
                    <button
                      data-testid="profile-bio-cancel"
                      onClick={() => { setBio(user?.bio || ""); setEditingBio(false); }}
                      className="px-4 py-2 rounded-full text-sm font-bold text-slate-600 hover:text-slate-900"
                    >Zrušit</button>
                    <button
                      data-testid="profile-bio-save"
                      disabled={saving}
                      onClick={async () => {
                        setSaving(true);
                        try {
                          await api.patch("/users/me", { bio });
                          await refresh();
                          toast.success("Uloženo");
                          setEditingBio(false);
                        } catch (e) {
                          toast.error(friendlyError(e));
                        } finally { setSaving(false); }
                      }}
                      className="px-4 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-60"
                    >{saving ? "Ukládám…" : "Uložit"}</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2">
                {user.bio ? (
                  <p data-testid="profile-bio-text" className="text-sm text-slate-700 font-medium leading-relaxed">{user.bio}</p>
                ) : (
                  <p className="text-sm text-slate-400 italic">Jednou větou napiš, kdo jsi a co tě baví. Aby tě lidi poznali na první pohled.</p>
                )}
                <button
                  data-testid="profile-bio-edit"
                  onClick={() => setEditingBio(true)}
                  className="mt-2 text-xs text-sky-600 font-bold underline underline-offset-4"
                >{user.bio ? "Upravit" : "Přidat"}</button>
              </div>
            )}
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            {status !== "approved" && (
              <Link
                data-testid="profile-verify-cta"
                to="/verify"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold transition-all active:scale-95"
              >
                <ShieldCheck className="w-4 h-4" /> {status === "submitted" ? "Stav ověření" : "Nahrát občanku"}
              </Link>
            )}
            {user.is_admin && (
              <Link
                data-testid="profile-admin-cta"
                to="/admin"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold transition-all"
              >
                <Settings className="w-4 h-4" /> Admin panel
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Item({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/40">
      <div className="flex items-center gap-1.5 text-slate-500 text-xs uppercase tracking-[0.12em] font-bold">
        {icon} {label}
      </div>
      <div className="mt-1 font-bold text-slate-900">{value || "—"}</div>
    </div>
  );
}
